import { Link, Modal } from "@caprikorn/dynamo-ui";
import type { FC } from "react";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useTenant } from "@contexts/Tenant/Tenant.context";
import { MSPRO_SUPPORT_EMAIL } from "@core/core.constants";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { phoneNumberSeparator } from "@utils/text.utils";
import { CustomTrans } from "@utils/translations/CustomTrans";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./HelpModal.module.css";
import type { HelpModalProps } from "./HelpModal.types";

export const HelpModal: FC<HelpModalProps> = ({
  onClose,
  isGroupDetailsQueryEnabled = true,
}) => {
  const { t } = useCustomTranslation("main");
  const {
    freshdeskSupportUrl,
    supportEmail: defaultSupportEmail,
    supportPhone = "",
  } = useTenant();
  const { currentGroup } = useGroupReferences();

  const { data } = useGetGroup({
    groupId: currentGroup,
    options: {
      enabled: isGroupDetailsQueryEnabled,
    },
  });

  const supportEmail = data?.group.email || defaultSupportEmail;
  const hasPhoneSupport = Boolean(supportPhone);

  const phone = phoneNumberSeparator(
    data?.group.customerServicePhoneNumber || supportPhone,
  );

  return (
    <Modal maxSize="md" onClose={onClose} title={t("helpModal.title")}>
      <Modal.Header
        illustration={hasPhoneSupport ? "customerService" : "mail"}
        subtitle={t("helpModal.subtitle")}
        title={t("helpModal.title")}
      />
      <Modal.Body className={classes.content}>
        <p>
          {supportEmail !== MSPRO_SUPPORT_EMAIL ? (
            <CustomTrans
              components={{
                supportLink: <Link href={`mailto:${supportEmail}`} />,
              }}
              i18nKey="helpModal.contactEmail"
              ns="main"
              values={{ label: supportEmail }}
            />
          ) : (
            <CustomTrans
              components={{
                supportLink: (
                  <Link
                    href={freshdeskSupportUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                  />
                ),
              }}
              i18nKey="helpModal.contactForm"
              ns="main"
            />
          )}{" "}
          {hasPhoneSupport && t("helpModal.phoneContact")}
        </p>
        {hasPhoneSupport && (
          <>
            <p>
              <CustomTrans
                components={{
                  phone: <Link href={`tel:${phone}`} size="lg" />,
                  sup: <sup />,
                }}
                i18nKey="helpModal.phoneNumber"
                ns="main"
                values={{ phone }}
              />
            </p>
            <p className="font-caption">
              <CustomTrans
                components={{
                  sup: <sup />,
                }}
                i18nKey="helpModal.phoneInfo"
                ns="main"
              />
            </p>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
